import { IProduct, Review } from '@kizi/comparator-core';
import {
  // Button,
  Div,
  DivProps,
  List,
  ListItem,
  P,
} from '@kizi/core-components';
import { Anchor } from '@kizi/shared-components';
import React, { MouseEventHandler } from 'react';

// Constants
const RATING_THRESHOLDS = {
  MIN_DISPLAY_RATING: 3.5,
  MAX_REVIEWS_DISPLAYED: 2,
  TOTAL_STARS: 5,
  HALF_STAR_THRESHOLD: 0.5
} as const;

const SUPPLIER_IDS = {
  ZONDUURZAAM: '2250',
  SOLAR_CONCEPT: '2854'
} as const;

const SUPPORTED_TYPES = {
  HOME: 'home',
  HEALTH: 'health',
  ENERGY_AUCTION: 'energyAuction'
} as const;
import { CbLogo } from '../../../../CbLogo';
import EmptyStarIcon from '../../../../Icons/EmptyStarIcon';
import FullStarIcon from '../../../../Icons/FullStarIcon';
import HalfStarIcon from '../../../../Icons/HalfStarIcon';
import ThumbsUpIcon from '../../../../Icons/ThumbsUpIcon';
import { LogoInsurance } from '../../../../LogoInsurance';
import { OsLogo } from '../../../../OsLogo';
import { MoreInfoButton } from '../../../../Product/ExpandedPart';
import { RatingLeafComp } from '../../../../Product/ExpandedPart/Rating';
import { ReviewsFC } from '../../../../Product/ExpandedPart';
import { ProductTestimonials } from '../../../../ProductTestimonials';
import { getDisplayedScore } from '../../../../Sustainability';

interface ReviewsTabProps extends DivProps {
  data: IProduct;
  onButtonClick: MouseEventHandler<Element>;
  isOsHome?: boolean;
  hideTestoordeel?: boolean;
}

export const ReviewsTab: React.FC<ReviewsTabProps> = ({
  data,
  onButtonClick,
  hideTestoordeel,
  ...rest
}) => {
  const { review, provider, testScore, type } = data;

  const {
    sustainabilityCommerceScore,
    sustainabilityInvestmentScore,
    sustainabilityScore,
  } = provider;
  const showTestScoreWrapper = [SUPPORTED_TYPES.HOME, SUPPORTED_TYPES.HEALTH].includes(type as any);

  const moreInfoBtn = (
    <MoreInfoButton onClick={onButtonClick} text="Lees alle beoordelingen" />
  );

  const reviewsComponent = (
    <ReviewsFC review={review} title="Klantbeoordeling" />
  );

  const generateScoreWrapper = () => {
    return type === SUPPORTED_TYPES.ENERGY_AUCTION ? (
      <ScoreWrapper
        score={sustainabilityScore || '-,-'}
        description={
          'Het jaarlijkse duurzaamheidsonderzoek wordt vervangen door nieuw onderzoek. In afwachting van de resultaten is er geen score.'
        }
        secondSubtitle="Nieuw onderzoek"
        subtitle="Duurzaamheidsscore"
        title="Duurzaamheidsscore"
      >
        {sustainabilityScore && (
          <Div>
            <ListItem>
              <RatingLeafComp value={sustainabilityScore || '-,-'} />
              <div>Samenstelling product volgens stroometiket.</div>
            </ListItem>
            <ListItem>
              <RatingLeafComp value={sustainabilityInvestmentScore as string} />
              <div>Investeringsbeleid aanbieder.</div>
            </ListItem>
            <ListItem>
              <RatingLeafComp value={sustainabilityCommerceScore as string} />
              <div>Productie/inkoop/levering aanbieder.</div>
            </ListItem>
          </Div>
        )}
      </ScoreWrapper>
    ) : (
      <ScoreWrapper
        description={
          <P>
            Deze duurzaamheidsscore laat zien hoe verantwoord (of niet!) een
            verzekeraar jouw premiegeld belegt. Klik{' '}
            <Anchor
              variant="inputFootnote"
              target="_blank"
              href=" https://www.consumentenbond.nl/opstalverzekering/hoe-duurzaam-is-jouw-verzekeraar"
            >
              hier
            </Anchor>{' '}
            voor meer informatie.
          </P>
        }
        logo={'insurance'}
        score={sustainabilityScore || '-,-'}
        subtitle="Duurzaamheidsscore"
        title="Duurzaamheidsscore"
      />
    );
  };

  return (
    <>
      <Div display={[null, null, 'flex']} {...rest}>
        {type.includes('energy') && reviewsComponent}
        {showTestScoreWrapper && !hideTestoordeel && (
          <TestScoreWrapper
            type={type}
            score={String(testScore)}
          />
        )}
        {generateScoreWrapper()}
      </Div>
      {Boolean(testScore) && type !== SUPPORTED_TYPES.ENERGY_AUCTION && (
        <>
          <Div variant="moreInfoReview"></Div>
          <Div display={[null, null, 'flex']}>
            {reviewsComponent}
            <div />
          </Div>
        </>
      )}
      {review && <ReviewWrapper review={review}>{moreInfoBtn}</ReviewWrapper>}
    </>
  );
};

const calculateStars = (rating: number | string) => {
  const numericValue = parseFloat(String(rating));
  const fullStars = Math.floor(numericValue);

  const decimalPart = numericValue % 1;
  const halfStar = decimalPart > 0 && decimalPart <= RATING_THRESHOLDS.HALF_STAR_THRESHOLD ? 1 : 0;

  const emptyStars = RATING_THRESHOLDS.TOTAL_STARS - (fullStars + halfStar);

  return (
    <Div display="flex" role="img" aria-label={`Rating: ${numericValue} out of ${RATING_THRESHOLDS.TOTAL_STARS} stars`}>
      {Array.from({ length: fullStars }, (_, index) => (
        <FullStarIcon key={`full-${index}`} aria-hidden="true" />
      ))}
      {Array.from({ length: halfStar }, (_, index) => (
        <HalfStarIcon key={`half-${index}`} aria-hidden="true" />
      ))}
      {Array.from({ length: emptyStars }, (_, index) => (
        <EmptyStarIcon key={`empty-${index}`} aria-hidden="true" />
      ))}
    </Div>
  );
};

export const SolarPanelsReviewsTab: React.FC<ReviewsTabProps> = ({
  data,
  onButtonClick,
  ...rest
}) => {
  let reviews: {
    rating: number;
    date: string;
    title: string;
    description: string;
    confirmation: string;
  }[] = [];

  const reviewsZonduurzaam = [
    {
      rating: 5,
      date: '28-6-2023',
      title: 'Vriendelijk en vlekkeloos verlopen',
      description:
        'Panelen waren zo gezet door de mannen, zelfs op een snikhete dag. Werd keurig verteld dat de electricien wat later vertraging had, maar ook dat viel echt reuze mee. Vriendelijke mensen, zowel aan de telefoon als hier te werk. kreeg uitleg over dingen die ik niet mocht vergeten (zoals aanmelden op energieleveren) en ik heb ook fijne afspraken kunnen maken mbt betaling van de factuur. En dat voor een prima prijs!',
      confirmation: 'Ja, ik beveel dit aan',
    },
    {
      rating: 4.5,
      date: '23-6-2023',
      title: 'Correct en deskundig',
      description:
        'Vijf stuks zonnepanelen gekocht. Voortraject werd goed en deskundig begeleid. Snel contact, zowel via telefoon als de de mail. Er werd actief meegedacht. Dit geldt ook voor het plaatsen van de panelen. Deskundig, rustig en beleefde monteurs. Snelle werkers.',
      confirmation: 'Ja, ik beveel dit aan',
    },
  ];

  const reviewsSolarConcept = [
    {
      rating: 5,
      date: '03-04-2023',
      title: 'Ga zo door',
      description:
        'Alles van A tot Z naar wens, ook de info van een en ander is vlekkeloos verlopen. Ik zou als ik opnieuw mocht kiezen, weer met jullie in zee gaan.',
      confirmation: 'Ja, ik beveel dit aan',
    },
    {
      rating: 4.5,
      date: '10-02-2024',
      title: 'Volgens afspraak, veilig en door vakmensen!',
      description:
        'Er is goed geluisterd naar mij als klant. Het werk is volgens afspraak, veilig, snel en door vakmensen uitgevoerd. Contact maken met medewerkers van Solar Concept is goed geregeld. Als je belt dan is er snel contact en wordt je prima geholpen.',
      confirmation: 'Ja, ik beveel dit aan',
    },
  ];

  const reviews365Zon = [
    {
      rating: 5,
      date: '17-2-2023',
      title: 'Goede en vakkundige installatie',
      description:
        'Vanaf het moment van offerte tot en met de installatie werd er duidelijk gecommuniceerd. De installatie was conform afspraken. Ben blij met de werkwijze van 365zon.',
      confirmation: 'Ja, ik beveel dit aan',
    },
    {
      rating: 4.5,
      date: '23-6-2023',
      title: 'Geweldige aanwinst',
      description:
        'We zijn erg blij met de zonnepanelen. Ze zijn goed aangelegd en liggen mooi verdeeld op het dak. We hebben ook goede ervaring met de after sales toen bleek dat een pan niet goed lag. Deze werd zonder meer per omgaande goed gelegd. Klasse. Vrijwel dagelijks kijken we even via de app naar de opbrengst van de dag. Een geweldige aanwinst.',
      confirmation: 'Ja, ik beveel dit aan',
    },
  ];

  if (data.supplierExternalId === SUPPLIER_IDS.ZONDUURZAAM) {
    reviews = [...reviewsZonduurzaam];
  } else if (data.supplierExternalId === SUPPLIER_IDS.SOLAR_CONCEPT) {
    reviews = [...reviewsSolarConcept];
  } else {
    reviews = [...reviews365Zon];
  }

  return (
    <>
      <P variant="bodyImportant">{'Reviews van klanten'}</P>
      <Div
        display="flex"
        flexDirection={['column', 'column', 'row']}
        gridGap={3}
        my={3}
        {...rest}
      >
        {reviews
          .filter((el) => el.rating >= RATING_THRESHOLDS.MIN_DISPLAY_RATING)
          .slice(0, RATING_THRESHOLDS.MAX_REVIEWS_DISPLAYED)
          .map((el) => (
            <Div
              key={el.title}
              m={'0px!important'}
              p={4}
              borderRadius="16px"
              border="1px solid #F0F0EF"
              boxShadow="0px 2px 8px 0px rgba(0, 0, 0, 0.10)"
            >
              <Div display="flex" justifyContent="space-between" mb={4}>
                <P
                  variant="footnoteRegularLightGrey"
                  fontSize="12px"
                  fontWeight="300"
                >
                  {calculateStars(el.rating)}
                </P>
                <P
                  variant="footnoteRegularLightGrey"
                  fontSize="12px"
                  fontWeight="300"
                >
                  {el.date}
                </P>
              </Div>
              <P variant="bodyImportant" fontSize="14px!important" mb={3}>
                {el.title}
              </P>
              <P variant="footnoteRegularLightGrey" fontWeight="300" mb={3}>
                {el.description}
              </P>
              <Div display="flex" gridGap={2}>
                <P
                  variant="footnoteRegularLightGrey"
                  fontSize="12px"
                  fontWeight="300"
                >
                  {el.confirmation}
                </P>
                <ThumbsUpIcon aria-label="Recommended" />
              </Div>
            </Div>
          ))}
      </Div>
      {/* <Button
        variant="transparent"
        fontSize="14px!important"
        fontWeight="700"
        mt={2}
        mb={['16px', '16px', 0]}
      >
        {'Lees alle beoordelingen'}
      </Button> */}
    </>
  );
};

const TestScoreWrapper = ({ type, score }) => {
  let insurenceType = 'De Consumentenbond beoordeelt woonverzekeringen op de kwaliteit van de polisvoorwaarden. De belangrijkste dekkingsonderdelen krijgen van onze experts een score en een wegingspercentage. Zo berekenen wij dit Testoordeel.';

  if (type === SUPPORTED_TYPES.HOME && score === '0') {
    insurenceType =
      'Momenteel worden de nieuwe beoordelingen van de Consumentenbond over de woonverzekeringen verwerkt in de woonverzekeringsvergelijker.';
  }
  return score ? (
    <ScoreWrapper
      description={insurenceType}
      logo={'big'}
      score={String(score)}
      subtitle={'Testoordeel'}
      title={'Testoordeel'}
    />
  ) : (
    <ScoreWrapper
      description="We hebben op dit moment niet voldoende panelleden om een testoordeel te geven over deze verzekeraar."
      score={String(score)}
      subtitle="Testoordeel"
      title="Testoordeel"
    />
  );
};

type ReviewWrapperProps = {
  children: React.ReactChild;
  review: Review;
};

const ReviewWrapper: React.FC<ReviewWrapperProps> = ({ children, review }) => {
  return (
    <>
      <Div borderTop="1px solid" borderColor="second.1" pt={6} mt={6}>
        <ProductTestimonials
          display={['flex', null, 'none']}
          review={review}
          size={1}
        />
        <ProductTestimonials display={['none', null, 'flex']} review={review} />
      </Div>
      {children}
    </>
  );
};

type ScoreWrapperProps = {
  children?: React.ReactNode;
  description: string | React.ReactNode;
  logo?: 'big' | 'insurance' | 'vbdo' | 'os';
  score: string;
  secondSubtitle?: string;
  subtitle: string;
  title: string;
};

export const ScoreWrapper: React.FC<ScoreWrapperProps> = ({
  children,
  description,
  logo = 'big',
  score,
  secondSubtitle,
  subtitle,
  title,
}) => {
  const logoProps = { display: 'block', mb: 3, mx: 'auto' };
  return (
    <Div>
      <List variant="accordionProductInner">
        <ListItem>
          <Div variant="detailsListHeading">
            <P variant="bodyImportant">{title}</P>
          </Div>
          <Div bg="second.2" borderRadius={2} px={3}>
            <List variant="accordionProductMobile">
              <ListItem>
                {logo === 'big' && <CbLogo {...logoProps} />}
                {logo === 'insurance' && <LogoInsurance {...logoProps} />}
                {logo === 'vbdo' && (
                  <Div textAlign="center" {...logoProps}>
                    <img
                      alt="logo-vbdo"
                      src={`https://comparators.kizi.nl/assets/logo's/vbdo.png`}
                      width="auto"
                      height="32px"
                    />
                  </Div>
                )}
                {logo === 'os' && <OsLogo {...logoProps} />}
                <P mb={3}>
                  <strong>{`${subtitle}: ${getDisplayedScore(score)}`}</strong>
                </P>
                {description && secondSubtitle && (
                  <P>
                    <strong>{`${secondSubtitle}`}</strong>
                  </P>
                )}
                <Div>{description}</Div>
              </ListItem>
              {children}
            </List>
          </Div>
        </ListItem>
      </List>
    </Div>
  );
};
